import type { ErrorObject } from "ajv";
import validateRenderProfileStandalone from "./generated/document-model-react-render-profile-validator";
import type {
  AdmittedDocumentModel,
  AdmittedDocumentReactRenderInput,
  AdmittedDocumentRenderNode,
  AdmittedDocumentRenderResource,
  DocumentModelReactRenderInput,
  DocumentReactRenderAdmissionCode,
  DocumentRenderNodeInput,
  DocumentRenderResourceBytes,
} from "./document-model-types";

/**
 * The render profile validator is PRECOMPILED (Ajv standalone, generated by
 * `src/scripts/generate-document-model-render-profile-validator.ts`). It is
 * compiled here, and that is a correctness requirement rather than a
 * performance one: Ajv compiles schemas with `new Function(...)`
 * (`ajv/dist/compile/index.js:89`), and Studio's production CSP
 * (`apps/cogo-studio/public/_headers`) grants `script-src 'self'
 * 'wasm-unsafe-eval' 'unsafe-inline'` and NOT `'unsafe-eval'` --
 * `'wasm-unsafe-eval'` permits WebAssembly compilation and nothing else. A
 * runtime compile therefore throws in the page, and this module is reachable
 * from the package ROOT that Studio loads.
 *
 * `src/__tests__/document-model-admission-csp.test.ts` runs this module with
 * the `Function` constructor stubbed the way that CSP behaves, and
 * `src/__tests__/document-model-render-profile-validator-freshness.test.ts`
 * keeps the generated artifact in step with the two schemas it was built from.
 */
type StandaloneValidateFunction = ((value: unknown) => boolean) & {
  errors?: ErrorObject[] | null;
};

const validateRenderProfile =
  validateRenderProfileStandalone as unknown as StandaloneValidateFunction;

const BROWSER_SAFE_PICTURE_MEDIA_TYPES = new Set([
  "image/avif",
  "image/gif",
  "image/jpeg",
  "image/png",
  "image/webp",
]);

export class DocumentReactRenderAdmissionError extends Error {
  readonly name = "DocumentReactRenderAdmissionError";

  constructor(
    public readonly code: DocumentReactRenderAdmissionCode,
    message: string,
  ) {
    super(message);
  }
}

export async function admitDocumentReactRenderInput(
  input: DocumentModelReactRenderInput,
): Promise<AdmittedDocumentReactRenderInput> {
  try {
    validateOuterInput(input);
    if (!validateRenderProfile(input.document)) {
      throw schemaAdmissionError(validateRenderProfile.errors ?? []);
    }

    const document = structuredClone(input.document) as AdmittedDocumentModel;
    validateNodeSemantics(document);

    const declaredResources = uniqueById(document.resources, "declared");
    const suppliedResources = uniqueById(input.resources, "supplied");
    const suppliedResourceSnapshots =
      snapshotSuppliedResources(suppliedResources);
    const admittedResources: Record<string, AdmittedDocumentRenderResource> =
      {};

    for (const page of document.pages) {
      for (const node of page.nodes) {
        if (node.kind !== "picture") continue;
        const id = node.binaryItemIDRef;
        const declared = declaredResources.get(id);
        const supplied = suppliedResourceSnapshots.get(id);
        if (!declared || !supplied) {
          infrastructureError(
            `Picture ${node.id} requires exactly one declared and supplied resource ${id}.`,
          );
        }
        const resourceBytes = supplied.bytes;
        if (
          !declared.mediaType ||
          !BROWSER_SAFE_PICTURE_MEDIA_TYPES.has(declared.mediaType) ||
          declared.mediaType !== supplied.mediaType
        ) {
          infrastructureError(`Resource ${id} media type does not match.`);
        }
        if (
          !Number.isSafeInteger(declared.sizeBytes) ||
          declared.sizeBytes !== supplied.sizeBytes ||
          supplied.sizeBytes !== resourceBytes.byteLength
        ) {
          infrastructureError(`Resource ${id} byte size does not match.`);
        }
        if (!declared.sha256 || declared.sha256 !== supplied.sha256) {
          infrastructureError(
            `Resource ${id} declared SHA-256 does not match.`,
          );
        }
        const actualSha256 = await sha256Hex(resourceBytes);
        if (actualSha256 !== declared.sha256) {
          infrastructureError(`Resource ${id} byte SHA-256 does not match.`);
        }

        admittedResources[id] = Object.freeze({
          id,
          mediaType: declared.mediaType,
          sizeBytes: declared.sizeBytes,
          sha256: declared.sha256,
          dataUrl: bytesToDataUrl(resourceBytes, declared.mediaType),
        });
      }
    }

    deepFreeze(document);
    Object.freeze(admittedResources);
    return Object.freeze({ document, resources: admittedResources });
  } catch (error) {
    if (error instanceof DocumentReactRenderAdmissionError) throw error;
    const detail = error instanceof Error ? error.message : String(error);
    throw new DocumentReactRenderAdmissionError(
      "blocked-infrastructure",
      `Document render admission infrastructure failure: ${detail}`,
    );
  }
}

function validateOuterInput(
  input: unknown,
): asserts input is DocumentModelReactRenderInput {
  if (
    !isRecord(input) ||
    !isRecord(input.document) ||
    !Array.isArray(input.resources)
  ) {
    infrastructureError(
      "Document render input must contain a document and resource array.",
    );
  }
  for (const resource of input.resources) {
    if (
      !isRecord(resource) ||
      typeof resource.id !== "string" ||
      resource.id.length === 0 ||
      typeof resource.mediaType !== "string" ||
      resource.mediaType.length === 0 ||
      !Number.isSafeInteger(resource.sizeBytes) ||
      (resource.sizeBytes as number) < 0 ||
      typeof resource.sha256 !== "string" ||
      !/^[0-9a-f]{64}$/.test(resource.sha256) ||
      !(resource.bytes instanceof Uint8Array)
    ) {
      infrastructureError(
        "Each supplied resource must have valid metadata and Uint8Array bytes.",
      );
    }
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function schemaAdmissionError(
  errors: ErrorObject[],
): DocumentReactRenderAdmissionError {
  const geometry = errors.some((error) =>
    /(?:\/pageSetup(?:\/|$)|\/frame(?:\/|$)|\/pages\/\d+\/(?:width|height)$)/.test(
      error.instancePath,
    ),
  );
  return new DocumentReactRenderAdmissionError(
    geometry ? "blocked-geometry-mismatch" : "blocked-unsupported-node",
    `DocumentModel is outside the React render profile: ${errorsText(errors)}`,
  );
}

/**
 * Byte-for-byte what `Ajv#errorsText` produced with its default options
 * (`separator: ", "`, `dataVar: "data"`, `ajv/dist/core.js:392-400`). The
 * message this feeds is the one callers already read, so it is reproduced
 * here rather than changed: dropping the runtime Ajv instance must not move
 * the wording.
 */
function errorsText(errors: ErrorObject[]): string {
  if (errors.length === 0) return "No errors";
  return errors
    .map((error) => `data${error.instancePath} ${error.message}`)
    .join(", ");
}

function validateNodeSemantics(document: AdmittedDocumentModel): void {
  for (const page of document.pages) {
    for (const rawNode of page.nodes) {
      const node = rawNode as AdmittedDocumentRenderNode;
      validateFiniteFrame(node, page.width, page.height);
      if (node.kind === "table") {
        for (const cell of node.cells) {
          if (
            cell.rowIndex + cell.rowSpan > node.rowCount ||
            cell.columnIndex + cell.columnSpan > node.columnCount
          ) {
            throw new DocumentReactRenderAdmissionError(
              "blocked-geometry-mismatch",
              `Table ${node.id} cell span exceeds its declared grid.`,
            );
          }
        }
      }
      if (node.kind === "chart") {
        const categoryCount = node.chartData.categories.length;
        for (const series of node.chartData.series) {
          if (
            series.values.length !== categoryCount ||
            series.values.some((value) => !Number.isFinite(value))
          ) {
            throw new DocumentReactRenderAdmissionError(
              "blocked-unsupported-node",
              `Chart ${node.id} data is not finite and category-aligned.`,
            );
          }
        }
        if (
          node.chartData.series.some((series) =>
            series.values.some((value) => value < 0),
          ) ||
          (node.chartData.type === "pie" && node.chartData.series.length !== 1)
        ) {
          throw new DocumentReactRenderAdmissionError(
            "blocked-unsupported-node",
            `Chart ${node.id} uses a sign or series shape the deterministic renderer does not support.`,
          );
        }
      }
    }
  }
}

function validateFiniteFrame(
  node: DocumentRenderNodeInput,
  pageWidth: number,
  pageHeight: number,
): void {
  const values = [
    node.frame.x,
    node.frame.y,
    node.frame.width,
    node.frame.height,
    pageWidth,
    pageHeight,
  ];
  if (values.some((value) => !Number.isFinite(value))) {
    throw new DocumentReactRenderAdmissionError(
      "blocked-geometry-mismatch",
      `Node ${node.id} has non-finite page geometry.`,
    );
  }
}

function uniqueById<T extends { id: string }>(
  resources: T[],
  source: "declared" | "supplied",
): Map<string, T> {
  const byId = new Map<string, T>();
  for (const resource of resources) {
    if (byId.has(resource.id)) {
      infrastructureError(
        `Resource id ${resource.id} is duplicated in ${source} resources.`,
      );
    }
    byId.set(resource.id, resource);
  }
  return byId;
}

function infrastructureError(message: string): never {
  throw new DocumentReactRenderAdmissionError(
    "blocked-infrastructure",
    message,
  );
}

function snapshotResourceBytes(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  const snapshot = new Uint8Array(bytes.byteLength);
  snapshot.set(bytes);
  return snapshot;
}

type SuppliedResourceSnapshot = Omit<DocumentRenderResourceBytes, "bytes"> & {
  bytes: Uint8Array<ArrayBuffer>;
};

function snapshotSuppliedResources(
  resources: Map<string, DocumentRenderResourceBytes>,
): Map<string, SuppliedResourceSnapshot> {
  return new Map(
    Array.from(resources, ([id, resource]) => [
      id,
      {
        id: resource.id,
        mediaType: resource.mediaType,
        sizeBytes: resource.sizeBytes,
        sha256: resource.sha256,
        bytes: snapshotResourceBytes(resource.bytes),
      },
    ]),
  );
}

async function sha256Hex(bytes: Uint8Array<ArrayBuffer>): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    infrastructureError("Web Crypto SHA-256 is unavailable.");
  }
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes.buffer);
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}

function bytesToDataUrl(bytes: Uint8Array, mediaType: string): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return `data:${mediaType};base64,${btoa(binary)}`;
}

function deepFreeze(value: unknown): void {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) return;
  Object.freeze(value);
  for (const child of Object.values(value)) deepFreeze(child);
}
